// (c) chaehongee. Confidential and proprietary.

import { useState, type ReactElement } from 'react'
import { Globe, Lock } from 'lucide-react'
import styles from './PrivacyToggle.module.css'

interface PrivacyToggleProps {
    privacy: string
    canEdit: boolean
    onToggle: (newPrivacy: string) => void
    entityName?: string
}

export function PrivacyToggle({
    privacy,
    canEdit,
    onToggle,
    entityName = 'Session',
}: PrivacyToggleProps): ReactElement {
    const [isLoading, setIsLoading] = useState(false)

    async function handleClick() {
        if (!canEdit || isLoading) return

        setIsLoading(true)
        try {
            const newPrivacy = privacy === 'public' ? 'private' : 'public'
            await onToggle(newPrivacy)
        } finally {
            setIsLoading(false)
        }
    }

    const isPublic = privacy === 'public'
    const Icon = isPublic ? Globe : Lock
    const label = isPublic ? 'Public' : 'Private'

    if (!canEdit) {
        return (
            <div
                className={`${styles.badge} ${isPublic ? styles.public : styles.private}`}
                title={`${entityName} is ${label}`}
            >
                <Icon className={styles.icon} />
                <span className={styles.label}>{label}</span>
            </div>
        )
    }

    return (
        <button
            className={`${styles.toggle} ${isPublic ? styles.public : styles.private} ${isLoading ? styles.loading : ''}`}
            onClick={handleClick}
            title={`Click to make ${isPublic ? 'private' : 'public'}`}
            aria-pressed={isPublic}
            disabled={isLoading}
        >
            <Icon className={styles.icon} />
            <span className={styles.label}>{label}</span>
        </button>
    )
}

